/**
 * Campaign Export Modal
 *
 * Modal for exporting campaign data to platform-specific CSV formats.
 * Supports batch export to multiple platforms with ZIP download.
 *
 * Usage:
 *   <ExportModal
 *     isOpen={isOpen}
 *     onClose={() => setIsOpen(false)}
 *     campaignData={campaignData}
 *   />
 */

import React, { useState, useMemo, useEffect } from 'react';
import { Modal } from '@/components/ui/Modal';
import { Button } from '@/components/ui/Button';
import { useCampaignExport } from '@/hooks/useCampaignExport';
import {
  getAvailablePlatforms,
  CampaignData,
  Platform,
  PlatformInfo,
} from '@/lib/csv-export';
import { Download, Check, AlertTriangle, X, FileText, Archive } from 'lucide-react';

export interface ExportModalProps {
  /** Whether modal is open */
  isOpen: boolean;
  /** Callback when modal should close */
  onClose: () => void;
  /** Campaign data to export */
  campaignData: CampaignData;
  /** Pre-selected platforms (optional) */
  preselectedPlatforms?: Platform[];
}

/**
 * Platform selection card
 */
const PlatformCard: React.FC<{
  platform: PlatformInfo;
  selected: boolean;
  disabled: boolean;
  onToggle: () => void;
}> = ({ platform, selected, disabled, onToggle }) => {
  return (
    <button
      type="button"
      onClick={onToggle}
      disabled={disabled}
      className={`
        relative p-4 rounded-lg border-2 text-left transition-all duration-200
        ${selected
          ? 'border-primary bg-primary/10 dark:bg-primary/20'
          : 'border-border-light dark:border-border-dark hover:border-gray-400'
        }
        ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
      `}
    >
      {selected && (
        <div className="absolute top-2 right-2">
          <Check className="w-5 h-5 text-primary" />
        </div>
      )}
      <div className="flex items-center gap-3">
        <span className="text-2xl">{platform.icon}</span>
        <div>
          <div className="font-medium text-text-primaryLight dark:text-text-primaryDark">
            {platform.name}
          </div>
          <div className="text-sm text-text-secondaryLight dark:text-text-secondaryDark">
            {platform.description}
          </div>
        </div>
      </div>
    </button>
  );
};

/**
 * Export result item
 */
const ExportResultItem: React.FC<{
  platform: Platform;
  platformInfo: PlatformInfo;
  success: boolean;
  warnings: string[];
  errors: string[];
  onDownload: () => void;
}> = ({ platform, platformInfo, success, warnings, errors, onDownload }) => {
  return (
    <div
      className={`
        p-3 rounded-lg border
        ${success
          ? 'border-ok/30 bg-ok-soft'
          : 'border-bad/30 bg-bad-soft'
        }
      `}
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span>{platformInfo.icon}</span>
          <span className="font-medium">{platformInfo.name}</span>
          {success ? (
            <Check className="w-4 h-4 text-ok" />
          ) : (
            <X className="w-4 h-4 text-bad" />
          )}
        </div>
        {success && (
          <Button
            variant="ghost"
            size="sm"
            icon={<Download className="w-4 h-4" />}
            onClick={onDownload}
          >
            Download
          </Button>
        )}
      </div>
      {warnings.length > 0 && (
        <div className="mt-2 text-sm text-warn">
          {warnings.map((w, i) => (
            <div key={i} className="flex items-start gap-1">
              <AlertTriangle className="w-3 h-3 mt-0.5 flex-shrink-0" />
              <span>{w}</span>
            </div>
          ))}
        </div>
      )}
      {errors.length > 0 && (
        <div className="mt-2 text-sm text-bad">
          {errors.map((e, i) => (
            <div key={i}>{e}</div>
          ))}
        </div>
      )}
    </div>
  );
};

/**
 * Upload instructions for each platform
 */
const UPLOAD_INSTRUCTIONS: Record<Platform, { steps: string[]; helpLink: string }> = {
  meta: {
    steps: [
      'Go to Meta Ads Manager (business.facebook.com)',
      'Click "Import & Export" in the top menu',
      'Select "Import Ads" and upload the CSV',
      'Review and publish when ready',
    ],
    helpLink: 'https://www.facebook.com/business/help/import-export',
  },
  google_ads: {
    steps: [
      'Open Google Ads Editor (desktop app)',
      'Go to Account → Import → From CSV',
      'Upload the file and review changes',
      'Click "Post" to push to Google Ads',
    ],
    helpLink: 'https://support.google.com/google-ads/editor/answer/30564',
  },
  twitter: {
    steps: [
      'Go to Twitter Ads (ads.twitter.com)',
      'Navigate to Tools → Ads Editor',
      'Upload CSV and review',
      'Confirm to create campaigns',
    ],
    helpLink: 'https://business.twitter.com/en/help/campaign-editing-and-optimization/ads-editor.html',
  },
  pinterest: {
    steps: [
      'Go to Pinterest Ads Manager',
      'Click Ads → Bulk Editor',
      'Upload CSV and wait for processing',
      'Review results and fix any errors',
    ],
    helpLink: 'https://help.pinterest.com/en/business/article/use-the-bulk-editor',
  },
};

export const ExportModal: React.FC<ExportModalProps> = ({
  isOpen,
  onClose,
  campaignData,
  preselectedPlatforms,
}) => {
  // State
  const [selectedPlatforms, setSelectedPlatforms] = useState<Platform[]>(
    preselectedPlatforms || []
  );
  const [format, setFormat] = useState<'csv' | 'xlsx'>('csv');
  const [showInstructions, setShowInstructions] = useState<Platform | null>(null);

  // Export hook
  const {
    isExporting,
    results,
    errors,
    exportBatch,
    downloadResult,
    downloadAll,
    clearResults,
    getSummary,
  } = useCampaignExport(campaignData);

  // Get available platforms
  const availablePlatforms = useMemo(() => getAvailablePlatforms(), []);

  // Get platform info by ID
  const getPlatformInfo = (id: Platform) =>
    availablePlatforms.find((p) => p.id === id);

  // Reset on close
  useEffect(() => {
    if (!isOpen) {
      clearResults();
      setShowInstructions(null);
    }
  }, [isOpen, clearResults]);

  // Toggle platform selection
  const togglePlatform = (platform: Platform) => {
    setSelectedPlatforms((prev) =>
      prev.includes(platform)
        ? prev.filter((p) => p !== platform)
        : [...prev, platform]
    );
  };

  // Select all platforms
  const selectAll = () => {
    setSelectedPlatforms(availablePlatforms.map((p) => p.id));
  };

  // Clear selection
  const clearSelection = () => {
    setSelectedPlatforms([]);
  };

  // Handle export
  const handleExport = async () => {
    await exportBatch(selectedPlatforms, format);
  };

  // Get summary
  const summary = getSummary();
  const hasResults = results.size > 0;
  const hasSuccessfulResults = summary.successful > 0;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Export Campaign for Upload"
      size="lg"
    >
      <Modal.Body className="space-y-6">
        {/* Instructions banner */}
        {!hasResults && (
          <div className="p-4 bg-work-soft rounded-lg border border-work/30">
            <p className="text-sm text-work">
              Export your campaign as CSV files formatted for each advertising platform's
              bulk upload tool. Media assets will be included as URLs.
            </p>
          </div>
        )}

        {/* Platform Selection (show if no results yet) */}
        {!hasResults && (
          <>
            <div>
              <div className="flex items-center justify-between mb-3">
                <label className="block text-sm font-medium text-text-primaryLight dark:text-text-primaryDark">
                  Select Platforms
                </label>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={selectAll}
                    className="text-sm text-primary hover:underline"
                  >
                    Select All
                  </button>
                  {selectedPlatforms.length > 0 && (
                    <>
                      <span className="text-ink-3">|</span>
                      <button
                        type="button"
                        onClick={clearSelection}
                        className="text-sm text-ink-3 hover:underline"
                      >
                        Clear
                      </button>
                    </>
                  )}
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {availablePlatforms.map((platform) => (
                  <PlatformCard
                    key={platform.id}
                    platform={platform}
                    selected={selectedPlatforms.includes(platform.id)}
                    disabled={isExporting}
                    onToggle={() => togglePlatform(platform.id)}
                  />
                ))}
              </div>
            </div>

            {/* Format Selection */}
            <div>
              <label className="block text-sm font-medium text-text-primaryLight dark:text-text-primaryDark mb-2">
                File Format
              </label>
              <div className="flex gap-4">
                <label className="flex items-center cursor-pointer">
                  <input
                    type="radio"
                    name="format"
                    value="csv"
                    checked={format === 'csv'}
                    onChange={() => setFormat('csv')}
                    className="mr-2 text-primary focus:ring-primary"
                  />
                  <span className="text-sm">
                    <span className="font-medium">CSV</span>
                    <span className="text-ink-3 ml-1">(Recommended)</span>
                  </span>
                </label>
                <label className="flex items-center cursor-pointer">
                  <input
                    type="radio"
                    name="format"
                    value="xlsx"
                    checked={format === 'xlsx'}
                    onChange={() => setFormat('xlsx')}
                    className="mr-2 text-primary focus:ring-primary"
                  />
                  <span className="text-sm">
                    <span className="font-medium">Excel (.xlsx)</span>
                    <span className="text-ink-3 ml-1">(Editable)</span>
                  </span>
                </label>
              </div>
            </div>
          </>
        )}

        {/* Export Results */}
        {hasResults && (
          <div className="space-y-4">
            {/* Summary */}
            <div
              className={`
                p-4 rounded-lg border
                ${summary.failed === 0
                  ? 'bg-ok-soft border-ok/30'
                  : summary.successful === 0
                  ? 'bg-bad-soft border-bad/30'
                  : 'bg-warn-soft border-warn/30'
                }
              `}
            >
              <div className="flex items-center gap-2">
                {summary.failed === 0 ? (
                  <Check className="w-5 h-5 text-ok" />
                ) : (
                  <AlertTriangle className="w-5 h-5 text-warn" />
                )}
                <span className="font-medium">
                  {summary.successful} of {summary.total} export
                  {summary.total !== 1 ? 's' : ''} ready
                </span>
              </div>
            </div>

            {/* Individual Results */}
            <div className="space-y-2">
              {Array.from(results.entries()).map(([platform, result]) => {
                const info = getPlatformInfo(platform);
                if (!info) return null;
                return (
                  <ExportResultItem
                    key={platform}
                    platform={platform}
                    platformInfo={info}
                    success={result.success}
                    warnings={result.warnings}
                    errors={result.errors}
                    onDownload={() => downloadResult(platform)}
                  />
                );
              })}
            </div>

            {/* Upload Instructions Toggle */}
            {hasSuccessfulResults && (
              <div>
                <button
                  type="button"
                  onClick={() =>
                    setShowInstructions(
                      showInstructions ? null : Array.from(results.keys())[0]
                    )
                  }
                  className="text-sm text-primary hover:underline flex items-center gap-1"
                >
                  <FileText className="w-4 h-4" />
                  {showInstructions ? 'Hide' : 'Show'} upload instructions
                </button>

                {showInstructions && (
                  <div className="mt-3 p-4 bg-ground dark:bg-card-darkAlt rounded-lg">
                    <div className="flex gap-2 mb-3 flex-wrap">
                      {Array.from(results.entries())
                        .filter(([, r]) => r.success)
                        .map(([platform]) => {
                          const info = getPlatformInfo(platform);
                          return (
                            <button
                              key={platform}
                              type="button"
                              onClick={() => setShowInstructions(platform)}
                              className={`
                                px-3 py-1 rounded-full text-sm
                                ${showInstructions === platform
                                  ? 'bg-primary text-white'
                                  : 'bg-inset hover:bg-gray-300'
                                }
                              `}
                            >
                              {info?.icon} {info?.name}
                            </button>
                          );
                        })}
                    </div>

                    {showInstructions && UPLOAD_INSTRUCTIONS[showInstructions] && (
                      <div>
                        <ol className="list-decimal list-inside space-y-1 text-sm text-text-secondaryLight dark:text-text-secondaryDark">
                          {UPLOAD_INSTRUCTIONS[showInstructions].steps.map(
                            (step, i) => (
                              <li key={i}>{step}</li>
                            )
                          )}
                        </ol>
                        <a
                          href={UPLOAD_INSTRUCTIONS[showInstructions].helpLink}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-block mt-2 text-sm text-primary hover:underline"
                        >
                          View platform documentation →
                        </a>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Global Errors */}
        {errors.length > 0 && (
          <div className="p-4 bg-bad-soft rounded-lg border border-bad/30">
            <div className="font-medium text-bad mb-1">
              Export Errors
            </div>
            <ul className="list-disc list-inside text-sm text-bad">
              {errors.map((error, i) => (
                <li key={i}>{error}</li>
              ))}
            </ul>
          </div>
        )}
      </Modal.Body>

      <Modal.Footer>
        <Button variant="ghost" onClick={onClose}>
          {hasResults ? 'Close' : 'Cancel'}
        </Button>

        {!hasResults ? (
          <Button
            variant="primary"
            onClick={handleExport}
            disabled={selectedPlatforms.length === 0 || isExporting}
            loading={isExporting}
            icon={<Download className="w-4 h-4" />}
          >
            {isExporting
              ? 'Generating...'
              : `Export ${selectedPlatforms.length} Platform${selectedPlatforms.length !== 1 ? 's' : ''}`}
          </Button>
        ) : hasSuccessfulResults ? (
          <Button
            variant="primary"
            onClick={downloadAll}
            icon={
              summary.successful > 1 ? (
                <Archive className="w-4 h-4" />
              ) : (
                <Download className="w-4 h-4" />
              )
            }
          >
            {summary.successful > 1
              ? `Download All (${summary.successful} files)`
              : 'Download File'}
          </Button>
        ) : (
          <Button
            variant="primary"
            onClick={() => {
              clearResults();
            }}
          >
            Try Again
          </Button>
        )}
      </Modal.Footer>
    </Modal>
  );
};

export default ExportModal;
